import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';
import { setSentryOrg } from '@/lib/sentry';

export type OrganizationRole = Database['public']['Enums']['organization_role'];

export interface MembershipWithOrg {
  id: string;
  organization_id: string;
  role: OrganizationRole;
  is_default: boolean;
  joined_at: string;
  organization: {
    id: string;
    name: string;
    slug: string;
    plan: string;
    trial_ends_at: string | null;
    country_code: string;
    settings: Record<string, unknown>;
  };
}

interface OrganizationContextType {
  loading: boolean;
  memberships: MembershipWithOrg[];
  currentOrganizationId: string | null;
  currentOrganization: MembershipWithOrg['organization'] | null;
  currentRole: OrganizationRole | null;
  switchOrganization: (organizationId: string) => void;
  refresh: () => Promise<void>;
}

const STORAGE_KEY = 'dryconfig.currentOrganizationId';

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useCurrentOrganization = () => {
  const ctx = useContext(OrganizationContext);
  if (ctx === undefined) {
    throw new Error('useCurrentOrganization must be used within an OrganizationProvider');
  }
  return ctx;
};

export const OrganizationProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEY);
  });

  const { data: memberships = [], isLoading, refetch } = useQuery({
    queryKey: ['memberships', user?.id],
    enabled: !!user,
    queryFn: async (): Promise<MembershipWithOrg[]> => {
      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          id,
          organization_id,
          role,
          is_default,
          joined_at,
          organization:organizations!organization_members_organization_id_fkey (
            id,
            name,
            slug,
            plan,
            trial_ends_at,
            country_code,
            settings
          )
        `)
        .eq('user_id', user!.id)
        .order('joined_at', { ascending: true });

      if (error) throw error;
      return (data ?? []) as unknown as MembershipWithOrg[];
    },
  });

  // When memberships load, ensure selectedOrgId is valid; otherwise pick the default.
  useEffect(() => {
    if (memberships.length === 0) {
      setSelectedOrgId(null);
      return;
    }
    const stillValid = selectedOrgId && memberships.some(m => m.organization_id === selectedOrgId);
    if (!stillValid) {
      const defaultMembership = memberships.find(m => m.is_default) ?? memberships[0];
      setSelectedOrgId(defaultMembership.organization_id);
    }
  }, [memberships, selectedOrgId]);

  // Persist selection (locale + lato server)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (selectedOrgId) localStorage.setItem(STORAGE_KEY, selectedOrgId);
    else localStorage.removeItem(STORAGE_KEY);
  }, [selectedOrgId]);

  // IMPORTANTE — l'org corrente va salvata anche sul PROFILO: il motore prezzi
  // (view materials_with_pricing, funzione current_org_id()) la usa per applicare
  // sconti e personalizzazioni della SOLA azienda su cui si sta lavorando.
  // Senza questo, un utente membro di più org vedeva prezzi diversi dai colleghi
  // sullo stesso preventivo (sconti presi da un'altra sua azienda).
  useEffect(() => {
    if (!user?.id || !selectedOrgId) return;
    let cancelled = false;
    (async () => {
      const { error } = await supabase
        .from('profiles')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update({ current_organization_id: selectedOrgId } as any)
        .eq('id', user.id);
      if (error && !cancelled) {
        console.warn('[OrganizationContext] current_organization_id non salvata:', error.message);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, selectedOrgId]);

  const switchOrganization = (organizationId: string) => {
    if (!memberships.some(m => m.organization_id === organizationId)) return;
    setSelectedOrgId(organizationId);
    // L'org corrente va scritta sul profilo PRIMA di ricaricare i dati: il motore
    // prezzi legge current_org_id() lato DB, quindi se invalidassimo subito le
    // query rischieremmo di rileggere i prezzi con l'org precedente.
    (async () => {
      if (user?.id) {
        await supabase
          .from('profiles')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .update({ current_organization_id: organizationId } as any)
          .eq('id', user.id);
      }
      queryClient.invalidateQueries();
    })();
  };

  const value = useMemo<OrganizationContextType>(() => {
    const currentMembership = memberships.find(m => m.organization_id === selectedOrgId) ?? null;
    return {
      loading: isLoading,
      memberships,
      currentOrganizationId: selectedOrgId,
      currentOrganization: currentMembership?.organization ?? null,
      currentRole: currentMembership?.role ?? null,
      switchOrganization,
      refresh: async () => { await refetch(); },
    };
  }, [memberships, selectedOrgId, isLoading]);

  // Sentry: tagga l'org corrente sulla sessione errori per filtrare bug
  // per organizzazione in dashboard.
  useEffect(() => {
    setSentryOrg(value.currentOrganizationId, value.currentOrganization?.name ?? null);
  }, [value.currentOrganizationId, value.currentOrganization?.name]);

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};
